import React from 'react';
import useSEO from '../HomePage/useSEO';
import Header from '../Header&Footer/Header';
import Footer from '../Header&Footer/Footer';
import { Button } from '@/Components/ui/button';
import CaseStudyProof, { caseStudyProofs } from '../../../Blog/CaseStudyProof';
import {
  Brain,
  Code,
  Rocket,
  Cog as Cogs,
  Bug,
  FlaskConical as Vial,
  Search,
  TrendingUp,
  Target,
  Globe,
  Database,
  Sparkles,
  ArrowRight,
  CheckCircle2,
} from 'lucide-react';

/**
 * Coding Assessment page — restyled to match the SearchJob.ai landing
 * theme: cream background, forest-green primary, gradient heading
 * accents (primary → emerald → teal), eyebrow pills, rounded-3xl
 * cards, pill CTAs, and dark-ink bands for the stats + final CTA.
 *
 * All copy, data arrays and links are preserved from the original page;
 * only the presentation was brought in line with the landing page.
 */

const INK = 'hsl(164 33% 7%)'; // brand dark-ink surface (Stats / CTA bands)

export default function CodingAssessment() {
  useSEO({
    title:
      'AI Coding Assessment Platform — Proctored Tests in 10 Languages | SearchJob.ai',
    description:
      'Run secure, proctored coding tests in 10 languages with AI follow-up interviews and cheating detection — auto-graded against hidden test cases.',
    canonical: 'https://searchjob.ai/hirewithus/coding-assessment',
    ogImage: 'https://searchjob.ai/og-image.png',
  });

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="pt-16">
        {/* ═════════════ HERO ═════════════ */}
        <section className="relative overflow-hidden bg-background isolate">
          {/* Gradient mesh + grid, matching the landing hero */}
          <div className="absolute inset-0 -z-10 pointer-events-none">
            <div className="absolute top-[-15%] left-[8%] w-[55%] h-[55%] bg-primary/15 rounded-full blur-3xl" />
            <div className="absolute top-[5%] right-[5%] w-[45%] h-[45%] bg-emerald-500/10 rounded-full blur-3xl" />
            <div className="absolute bottom-[-25%] left-1/2 -translate-x-1/2 w-[70%] h-[50%] bg-teal-500/10 rounded-full blur-3xl" />
          </div>
          <div
            className="absolute inset-0 -z-10 opacity-[0.12] pointer-events-none"
            style={{
              backgroundImage:
                'linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)',
              backgroundSize: '44px 44px',
              color: 'hsl(var(--foreground) / 0.1)',
              maskImage:
                'radial-gradient(ellipse 80% 60% at 50% 0%, black 30%, transparent 80%)',
              WebkitMaskImage:
                'radial-gradient(ellipse 80% 60% at 50% 0%, black 30%, transparent 80%)',
            }}
          />

          <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8 pt-16 lg:pt-20 pb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/20 bg-primary/5 text-primary text-xs font-semibold uppercase tracking-wider mb-6">
              <Sparkles className="w-3.5 h-3.5" />
              AI-Powered Coding Assessment Platform
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-[-0.02em] text-foreground leading-[1.08] mb-6">
              Evaluate technical talent with
              <br />
              <span className="bg-gradient-to-r from-primary via-emerald-600 to-teal-600 bg-clip-text text-transparent">
                precision and speed.
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-2xl mx-auto mb-8">
              Comprehensive, unbiased and real-time evaluation of coding skills,
              problem-solving abilities and technical proficiency — powered by
              cutting-edge artificial intelligence.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                asChild
                size="lg"
                className="!bg-primary !text-primary-foreground hover:!bg-primary/90 h-12 px-8 text-base font-semibold group !rounded-full shadow-lg shadow-primary/25"
              >
                <a href="/hirewithus/login">
                  Get Started
                  <ArrowRight className="w-4 h-4 ml-1.5 group-hover:translate-x-0.5 transition-transform" />
                </a>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="h-12 px-8 text-base font-semibold !rounded-full bg-card/70 backdrop-blur-md"
              >
                <a href="/hirewithus/contact">Contact Us</a>
              </Button>
            </div>
          </div>
        </section>

        {/* ═════════════ OVERVIEW ═════════════ */}
        <Section>
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <Eyebrow>Overview</Eyebrow>
              <SectionHeading plain="Revolutionising how you" accent="evaluate technical talent." />
              <p className="text-lg text-muted-foreground leading-relaxed mb-4">
                Our AI-powered coding assessment platform revolutionizes how
                organizations evaluate technical talent. Built with cutting-edge
                artificial intelligence, it provides comprehensive, unbiased, and
                real-time evaluation of coding skills, problem-solving abilities,
                and technical proficiency.
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                From automated code analysis to intelligent scoring algorithms,
                our platform ensures that only candidates with the right technical
                capabilities progress through your hiring pipeline, saving time
                and improving the quality of your technical hires.
              </p>
              <Button
                asChild
                size="lg"
                className="!bg-primary !text-primary-foreground hover:!bg-primary/90 !rounded-full h-12 px-8 font-semibold"
              >
                <a href="/hirewithus/login">Get Started</a>
              </Button>
            </div>
            <ImageCard
              src="https://images.unsplash.com/photo-1555066931-4365d14bab8c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"
              alt="AI-powered coding assessment interface"
            />
          </div>
        </Section>

        {/* ═════════════ KEY FEATURES ═════════════ */}
        <Section muted>
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <ImageCard
              src="https://images.unsplash.com/photo-1516116216624-53e697fedbea?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2128&q=80"
              alt="Real-time coding evaluation"
              className="lg:order-1"
            />
            <div className="lg:order-2">
              <Eyebrow>Key features</Eyebrow>
              <SectionHeading plain="Everything you need to" accent="assess with confidence." />
              <CheckList
                items={[
                  'AI-Powered Code Analysis - Intelligent evaluation of code quality, efficiency, and best practices',
                  'Real-Time Execution Environment - Live code compilation and testing in secure sandboxed environments',
                  'Automated Scoring System - Objective assessment based on multiple technical criteria',
                  'Customizable Assessment Templates - Role-specific coding challenges and problem sets',
                  'Plagiarism Detection - Advanced algorithms to ensure code originality and integrity',
                  'Performance Analytics - Detailed insights into candidate problem-solving approach and efficiency',
                ]}
              />
            </div>
          </div>
        </Section>

        {/* ═════════════ CORE CAPABILITIES ═════════════ */}
        <Section>
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Eyebrow center>Core capabilities</Eyebrow>
            <SectionHeading center plain="Essential tools for" accent="comprehensive evaluation." />
            <p className="text-lg text-muted-foreground leading-relaxed">
              Essential technical assessment tools for comprehensive candidate
              evaluation
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {[
              { icon: Brain, title: 'Problem Solving', desc: 'Algorithmic thinking & logic' },
              { icon: Code, title: 'Code Quality', desc: 'Readability & best practices' },
              { icon: Rocket, title: 'Performance', desc: 'Efficiency & optimization' },
              { icon: Cogs, title: 'System Design', desc: 'Architecture & scalability' },
              { icon: Bug, title: 'Debugging', desc: 'Problem identification' },
              { icon: Vial, title: 'Testing', desc: 'Quality assurance' },
            ].map((capability) => {
              const Icon = capability.icon;
              return (
                <div
                  key={capability.title}
                  className="bg-card border border-border/70 rounded-3xl p-6 shadow-sm hover:shadow-[0_12px_48px_-12px_hsl(var(--primary)/0.22)] hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="w-11 h-11 rounded-2xl bg-primary flex items-center justify-center shadow-lg shadow-primary/25 mb-4">
                    <Icon className="w-5 h-5 text-primary-foreground" />
                  </div>
                  <h3 className="text-base font-bold text-foreground mb-1.5 leading-snug">
                    {capability.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {capability.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </Section>

        {/* ═════════════ WHY CHOOSE OUR PLATFORM ═════════════ */}
        <Section muted>
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <ImageCard
              src="https://images.unsplash.com/photo-1559136555-9303baea8ebd?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"
              alt="AI technology innovation"
              className="lg:order-2"
            />
            <div className="lg:order-1">
              <Eyebrow>Why choose us</Eyebrow>
              <SectionHeading plain="Smarter assessments," accent="better technical hires." />
              <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                Traditional coding assessments often fall short in providing
                comprehensive evaluation and can be time-consuming to administer
                and grade. Our AI-powered solution addresses these challenges with
                intelligent automation and detailed insights.
              </p>
              <div className="space-y-4">
                {[
                  { num: '1', title: 'Instant Evaluation & Feedback', desc: 'Real-time assessment results with detailed performance analytics and improvement suggestions' },
                  { num: '2', title: 'Unbiased & Consistent Assessment', desc: 'AI-driven evaluation ensures fair and consistent scoring across all candidates' },
                  { num: '3', title: 'Scalable & Cost-Effective', desc: 'Handle multiple assessments simultaneously without additional resources or time investment' },
                ].map((benefit) => (
                  <div
                    key={benefit.num}
                    className="flex items-start gap-4 bg-card border border-border/70 rounded-3xl p-6 shadow-sm hover:shadow-[0_12px_48px_-12px_hsl(var(--primary)/0.22)] hover:-translate-y-1 transition-all duration-300"
                  >
                    <div className="shrink-0 w-11 h-11 rounded-2xl bg-gradient-to-br from-primary to-emerald-800 text-primary-foreground flex items-center justify-center font-black text-lg shadow-lg shadow-primary/25">
                      {benefit.num}
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-foreground mb-1.5 leading-snug">
                        {benefit.title}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {benefit.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Section>

        {/* ═════════════ STATS BAND (dark ink) ═════════════ */}
        <section className="relative py-14 lg:py-20 overflow-hidden" style={{ backgroundColor: INK }}>
          <div className="absolute top-[-20%] left-1/2 -translate-x-1/2 w-2/3 h-1/2 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
              {[
                { number: '70%', text: 'Accuracy in Code Evaluation' },
                { number: '5X', text: 'Faster Assessment Process' },
                { number: '50%', text: 'Reduction in Hiring Time' },
              ].map((stat) => (
                <div key={stat.text}>
                  <div className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent">
                    {stat.number}
                  </div>
                  <div className="mt-2 text-sm text-white/70">{stat.text}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═════════════ SUPPORTED TECHNOLOGIES ═════════════ */}
        <Section>
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Eyebrow center>Supported stack</Eyebrow>
            <SectionHeading center plain="Supported technologies" accent="& languages." />
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {[
              { icon: Code, title: 'Programming Languages' },
              { icon: Globe, title: 'Web Technologies' },
              { icon: Database, title: 'Databases & Cloud' },
            ].map((tech) => {
              const Icon = tech.icon;
              return (
                <div
                  key={tech.title}
                  className="bg-card border border-border/70 rounded-3xl p-8 shadow-sm hover:shadow-[0_12px_48px_-12px_hsl(var(--primary)/0.22)] hover:-translate-y-1 transition-all duration-300 flex flex-col items-center text-center"
                >
                  <div className="w-11 h-11 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="text-base font-bold text-foreground leading-snug">
                    {tech.title}
                  </h3>
                </div>
              );
            })}
          </div>
        </Section>

        {/* ═════════════ ADVANCED AI CAPABILITIES ═════════════ */}
        <Section muted>
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <ImageCard
              src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2071&q=80"
              alt="Team collaboration in tech"
              className="lg:order-1"
            />
            <div className="lg:order-2">
              <Eyebrow>Advanced AI</Eyebrow>
              <SectionHeading plain="Intelligence that goes" accent="beyond the code." />
              <div className="space-y-4">
                {[
                  { icon: Search, title: 'Intelligent Code Review', desc: 'AI-powered analysis of code structure, complexity, and adherence to industry best practices' },
                  { icon: TrendingUp, title: 'Performance Prediction', desc: 'Machine learning models that predict candidate performance based on coding patterns and problem-solving approaches' },
                  { icon: Target, title: 'Adaptive Difficulty', desc: 'Dynamic assessment adjustment based on candidate performance for optimal evaluation accuracy' },
                ].map((feature) => {
                  const Icon = feature.icon;
                  return (
                    <div
                      key={feature.title}
                      className="flex items-start gap-4 bg-card border border-border/70 rounded-3xl p-6 shadow-sm hover:shadow-[0_12px_48px_-12px_hsl(var(--primary)/0.22)] hover:-translate-y-1 transition-all duration-300"
                    >
                      <div className="shrink-0 w-11 h-11 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="text-base font-bold text-foreground mb-1.5 leading-snug">
                          {feature.title}
                        </h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {feature.desc}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </Section>

        <Section muted>
          <CaseStudyProof items={[caseStudyProofs.manest]} />
        </Section>

        {/* ═════════════ FINAL CTA (dark ink) ═════════════ */}
        <section className="relative py-16 lg:py-24 bg-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div
              className="relative overflow-hidden rounded-[2.5rem] p-10 lg:p-16 text-white shadow-2xl text-center"
              style={{ backgroundColor: INK }}
            >
              <div className="absolute top-[-30%] right-[-10%] w-[60%] h-[80%] bg-emerald-500/15 rounded-full blur-3xl pointer-events-none" />
              <div className="absolute bottom-[-30%] left-[-10%] w-[50%] h-[70%] bg-teal-500/10 rounded-full blur-3xl pointer-events-none" />
              <div className="relative max-w-2xl mx-auto">
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-5 leading-tight">
                  Ready to revolutionize your
                  <br />
                  <span className="bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent">
                    technical hiring?
                  </span>
                </h2>
                <p className="text-lg text-white/70 leading-relaxed mb-8">
                  Experience the future of coding assessments with AI-powered
                  precision, real-time evaluation, and comprehensive insights that
                  help you hire the best technical talent.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button
                    asChild
                    size="lg"
                    className="!bg-white !text-foreground hover:!bg-white/90 h-12 px-8 text-base font-semibold group shadow-xl !rounded-full"
                  >
                    <a href="/hirewithus/contact">
                      Contact Us
                      <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-0.5 transition-transform" />
                    </a>
                  </Button>
                  <Button
                    asChild
                    size="lg"
                    variant="outline"
                    className="!border-white/30 !bg-transparent !text-white hover:!bg-white/10 h-12 px-8 text-base font-semibold !rounded-full"
                  >
                    <a href="/hirewithus/login">Hire With Us</a>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────── */
/*  Small shared bits — mirror the landing page's building blocks    */
/* ─────────────────────────────────────────────────────────────── */
function Section({ children, muted = false }) {
  return (
    <section
      className={`relative py-14 lg:py-20 overflow-hidden ${
        muted ? 'bg-muted/30' : 'bg-background'
      }`}
    >
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {children}
      </div>
    </section>
  );
}

function Eyebrow({ children, center = false }) {
  return (
    <div
      className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/20 bg-primary/5 text-primary text-xs font-semibold uppercase tracking-wider mb-4 ${
        center ? 'mx-auto' : ''
      }`}
    >
      <Sparkles className="w-3.5 h-3.5" />
      {children}
    </div>
  );
}

function SectionHeading({ plain, accent, center = false }) {
  return (
    <h2
      className={`text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight text-foreground leading-[1.1] mb-5 ${
        center ? 'text-center' : ''
      }`}
    >
      {plain}{' '}
      <span className="bg-gradient-to-r from-primary via-emerald-600 to-teal-600 bg-clip-text text-transparent">
        {accent}
      </span>
    </h2>
  );
}

function CheckList({ items }) {
  return (
    <ul className="space-y-3">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-3">
          <span className="shrink-0 w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center mt-0.5">
            <CheckCircle2 className="w-3.5 h-3.5 text-primary" strokeWidth={2.5} />
          </span>
          <span className="text-base text-foreground/85 leading-relaxed">
            {item}
          </span>
        </li>
      ))}
    </ul>
  );
}

function ImageCard({ src, alt, className = '' }) {
  return (
    <div className={`relative ${className}`}>
      <div className="absolute -inset-4 bg-gradient-to-br from-primary/15 via-emerald-500/10 to-teal-500/15 blur-2xl rounded-full -z-10" />
      <img
        src={src}
        alt={alt}
        className="w-full max-w-[540px] mx-auto h-auto rounded-3xl border border-border shadow-2xl ring-1 ring-foreground/5"
      />
    </div>
  );
}
